import { X, Plus, Minus, Trash2, ShoppingCart } from "lucide-react";
import { useMemo } from "react";

const toNumber = (value, fallback = 0) => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

export default function CartSidebar({
  isOpen,
  onClose,
  cartItems,
  categoryName,
  onQtyChange,
  onPriceChange,
  onRemove,
  onClearAll,
  onContinue,
}) {
  const total = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.customPrice || 0), 0),
    [cartItems],
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
        aria-label="Close cart overlay"
      />

      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-[#e1f5fe] flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-[#005b9f]" />
            </div>
            <div>
              <p className="font-bold text-slate-900">Your Booking</p>
              <p className="text-xs text-slate-600">
                {categoryName ? `${categoryName} • ` : ""}
                {cartItems.length} item(s)
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition"
            aria-label="Close cart"
          >
            <X className="h-5 w-5 text-slate-700" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-10">
              <p className="font-semibold text-slate-800">Cart is empty</p>
              <p className="text-sm text-slate-600 mt-1">
                Add services to see them here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map((item) => {
                const itemId = item?.id || item?._id;
                const qty = item?.quantity ?? 1;
                const base = item?.basePrice ?? 0;
                const minPrice = base * qty;
                const currentPrice = toNumber(item?.customPrice, 0);

                return (
                  <div
                    key={itemId}
                    className="border rounded-xl p-3 bg-slate-50"
                  >
                    <div className="flex gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border flex-shrink-0">
                        <img
                          src={item?.image || "/placeholder.svg"}
                          alt={item?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">
                          {item?.name}
                        </p>

                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="number"
                            min={minPrice}
                            value={currentPrice}
                            onChange={(e) =>
                              onPriceChange(itemId, e.target.value, minPrice)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "Subtract")
                                e.preventDefault();
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="w-28 px-2 py-1 rounded-md border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-[#005b9f]"
                          />
                          <span className="text-xs text-slate-600 font-semibold">
                            Rs.
                          </span>

                          <button
                            type="button"
                            onClick={() => onRemove(itemId)}
                            className="ml-auto p-2 rounded-lg bg-white border hover:bg-red-50 hover:border-red-200 transition"
                            title="Remove"
                            aria-label="Remove item"
                          >
                            <Trash2 className="h-4 w-4 text-slate-700" />
                          </button>
                        </div>

                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onQtyChange(itemId, -1)}
                            className="p-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-4 w-4" />
                          </button>

                          <span className="text-sm font-bold text-slate-800 w-8 text-center">
                            {qty}
                          </span>

                          <button
                            type="button"
                            onClick={() => onQtyChange(itemId, 1)}
                            className="p-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white transition"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-4 w-4" />
                          </button>

                          <span className="ml-auto text-xs text-slate-600">
                            <span className="font-semibold">
                              Rs. {minPrice}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-700 font-semibold">Total</p>
            <p className="text-lg font-extrabold text-[#005b9f]">
              Rs. {total.toFixed(2)}
            </p>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onClearAll}
              disabled={cartItems.length === 0}
              className="flex-1 px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear all
            </button>

            <button
              type="button"
              onClick={onContinue}
              disabled={cartItems.length === 0}
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#005b9f] hover:bg-[#004a80] text-white font-extrabold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Proceed to Booking
            </button>
          </div>

          <p className="text-[11px] text-slate-500 mt-2">
            Edit quantity and price here. Continue when ready.
          </p>
        </div>
      </aside>
    </div>
  );
}
